// Promo discount system client-side helpers (Prompt 9 of the billing-flow
// series). Wraps the promo_codes + promo_redemptions tables, the
// validate_promo_code RPC, and the pending_promo_code column on competitions.
//
// Read helpers swallow errors → return null / [] so the UI never blocks on a
// missing row. Write helpers throw with the RPC's error message so call-
// sites can surface human-readable text in toasts / banners.

import { supabase } from "./supabase";

export type PromoDiscountType = "percent" | "fixed_amount";
export type PromoAppliesTo = "total" | "fees_only" | "scholarship_only";
export type PromoStatus = "active" | "paused" | "expired";

export type PromoCode = {
  id: string;
  code: string;
  description: string | null;
  internal_notes: string | null;
  discount_type: PromoDiscountType;
  discount_value: number;
  applies_to: PromoAppliesTo;
  max_total_uses: number | null;
  max_uses_per_client: number;
  current_use_count: number;
  valid_from: string | null;
  valid_until: string | null;
  min_order_cents: number | null;
  required_engagement_model: "licensing" | "rm" | null;
  required_tier: "100" | "250" | "500" | "unlimited" | null;
  required_university_id: string | null;
  status: PromoStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type PromoCodeInput = Omit<
  PromoCode,
  "id" | "current_use_count" | "created_by" | "created_at" | "updated_at"
>;

export type PromoValidation = {
  valid: boolean;
  reason: string;
  discount_cents: number;
  discount_label: string;
};

export type PromoRedemption = {
  id: string;
  promo_code_id: string;
  competition_id: string;
  quote_id: string;
  redeemed_by: string;
  amount_discounted_cents: number;
  applied_at: string;
  // Joined display fields, hydrated client-side
  client_name?: string | null;
  client_email?: string | null;
  competition_name?: string | null;
};

// ─── Reads ────────────────────────────────────────────────────────────────

export async function listPromoCodes(): Promise<PromoCode[]> {
  const { data, error } = await supabase
    .from("promo_codes")
    .select("*")
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data as PromoCode[];
}

export async function getPromoCode(id: string): Promise<PromoCode | null> {
  const { data, error } = await supabase
    .from("promo_codes")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error || !data) return null;
  return data as PromoCode;
}

/** Last 20 redemptions for a given code with client + case display info. */
export async function listRedemptionsForCode(promoCodeId: string): Promise<PromoRedemption[]> {
  const { data, error } = await supabase
    .from("promo_redemptions")
    .select("*")
    .eq("promo_code_id", promoCodeId)
    .order("applied_at", { ascending: false })
    .limit(20);
  if (error || !data) return [];
  const rows = data as PromoRedemption[];
  const userIds = Array.from(new Set(rows.map((r) => r.redeemed_by)));
  const compIds = Array.from(new Set(rows.map((r) => r.competition_id)));

  const [{ data: profiles }, { data: comps }] = await Promise.all([
    userIds.length === 0
      ? Promise.resolve({ data: [] as { id: string; full_name: string | null; email: string | null }[] })
      : supabase.from("user_profiles").select("id, full_name, email").in("id", userIds),
    compIds.length === 0
      ? Promise.resolve({ data: [] as { id: string; name: string }[] })
      : supabase.from("competitions").select("id, name").in("id", compIds),
  ]);

  const profileById = new Map((profiles ?? []).map((p) => [p.id as string, p as { full_name: string | null; email: string | null }]));
  const compById = new Map((comps ?? []).map((c) => [c.id as string, c.name as string]));

  return rows.map((r) => ({
    ...r,
    client_name: profileById.get(r.redeemed_by)?.full_name ?? null,
    client_email: profileById.get(r.redeemed_by)?.email ?? null,
    competition_name: compById.get(r.competition_id) ?? null,
  }));
}

/** Sum of revenue from quotes that redeemed this code. Used in the admin
 *  detail panel's analytics block. Reads the quotes rows joined by
 *  promo_redemptions. */
export async function getPromoAnalytics(promoCodeId: string): Promise<{
  redemption_count: number;
  total_discount_cents: number;
  total_revenue_cents: number;
}> {
  const { data: redemptions, error: redemptionsError } = await supabase
    .from("promo_redemptions")
    .select("amount_discounted_cents, quote_id")
    .eq("promo_code_id", promoCodeId);
  if (redemptionsError) {
    console.warn("[promos] redemptions analytics read failed:", redemptionsError.message);
  }
  const rows = redemptions ?? [];
  const quoteIds = rows.map((r) => r.quote_id as string);
  let revenueCents = 0;
  if (quoteIds.length > 0) {
    const { data: quotes, error: quotesError } = await supabase
      .from("quotes")
      .select("initial_payment_cents")
      .in("id", quoteIds);
    if (quotesError) {
      console.warn("[promos] quote revenue analytics read failed:", quotesError.message);
    }
    revenueCents = (quotes ?? []).reduce(
      (s, q) => s + (typeof q.initial_payment_cents === "number" ? q.initial_payment_cents : 0),
      0,
    );
  }
  return {
    redemption_count: rows.length,
    total_discount_cents: rows.reduce(
      (s, r) => s + (typeof r.amount_discounted_cents === "number" ? r.amount_discounted_cents : 0),
      0,
    ),
    total_revenue_cents: revenueCents,
  };
}

// ─── Validation ───────────────────────────────────────────────────────────

export async function validatePromoCode(
  code: string,
  competitionId: string,
): Promise<PromoValidation> {
  const { data, error } = await supabase.rpc("validate_promo_code", {
    p_code: code,
    p_competition_id: competitionId,
  });
  if (error) {
    return { valid: false, reason: error.message, discount_cents: 0, discount_label: "" };
  }
  const row = Array.isArray(data) ? data[0] : (data as PromoValidation | null);
  if (!row) return { valid: false, reason: "No response", discount_cents: 0, discount_label: "" };
  return {
    valid: !!row.valid,
    reason: row.reason ?? "",
    discount_cents: typeof row.discount_cents === "number" ? row.discount_cents : 0,
    discount_label: row.discount_label ?? "",
  };
}

// ─── Writes ───────────────────────────────────────────────────────────────

export async function createPromoCode(input: PromoCodeInput): Promise<string> {
  const { data, error } = await supabase
    .from("promo_codes")
    .insert({
      code: input.code.trim(),
      description: input.description?.trim() || null,
      internal_notes: input.internal_notes?.trim() || null,
      discount_type: input.discount_type,
      discount_value: input.discount_value,
      applies_to: input.applies_to,
      max_total_uses: input.max_total_uses,
      max_uses_per_client: input.max_uses_per_client,
      valid_from: input.valid_from,
      valid_until: input.valid_until,
      min_order_cents: input.min_order_cents,
      required_engagement_model: input.required_engagement_model,
      required_tier: input.required_tier,
      required_university_id: input.required_university_id,
      status: input.status,
    })
    .select("id")
    .single();
  if (error || !data) throw new Error(error?.message ?? "Failed to create promo code");
  return data.id as string;
}

export async function updatePromoCode(id: string, patch: Partial<PromoCodeInput>): Promise<void> {
  const { error } = await supabase
    .from("promo_codes")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

export async function deletePromoCode(id: string): Promise<void> {
  // Safety: only allow delete when 0 redemptions exist. The schema doesn't
  // enforce this but we check client-side; if the FK in promo_redemptions
  // prevents delete, the user gets a clear error.
  const { count } = await supabase
    .from("promo_redemptions")
    .select("id", { count: "exact", head: true })
    .eq("promo_code_id", id);
  if ((count ?? 0) > 0) {
    throw new Error("Code has redemptions — pause it instead of deleting.");
  }
  const { error } = await supabase.from("promo_codes").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// ─── Pending promo code on the case (client + admin both read this) ──────

export async function setPendingPromoCode(competitionId: string, code: string | null): Promise<void> {
  const { error } = await supabase
    .from("competitions")
    .update({ pending_promo_code: code ? code.trim().toUpperCase() : null })
    .eq("id", competitionId);
  if (error) throw new Error(error.message);
}

export async function getPendingPromoCode(competitionId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("competitions")
    .select("pending_promo_code")
    .eq("id", competitionId)
    .maybeSingle();
  if (error || !data) return null;
  return (data.pending_promo_code as string | null) ?? null;
}

// ─── Code generator (Generate random code button) ────────────────────────

export function generateRandomPromoCode(length = 8): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // drop 0/O/I/1 for legibility
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}
